import { useDocumentPictureInPicture } from '@/hooks/use-document-picture-in-picture';
import { CoachPortalSession } from '@airreading/common/data';
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';
import { InAppAssessmentWindow } from './in-app-assessment-window';

interface InAppAssessmentContextValue {
  openSessionId: string | null;
  open: (session: CoachPortalSession) => Promise<void>;
  close: () => void;
}

const InAppAssessmentContext = createContext<InAppAssessmentContextValue | null>(null);

/** Matches the in-page floating card so the PiP window is usable on a laptop beside Zoom. */
const IN_APP_ASSESSMENT_PIP_WINDOW_SIZE = { width: 480, height: 640 };

/**
 * Keeps a single assessment window mounted across `/my-classes` tabs.
 *
 * Triggers live in short-lived surfaces — a calendar popover closes the moment you click inside it —
 * so they hand the session to this provider instead of rendering the window themselves.
 *
 * Picture-in-Picture is requested in `open()` so it stays inside the Begin-assessment click gesture.
 */
export function InAppAssessmentProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<CoachPortalSession | null>(null);
  const dismissAssessment = useCallback(() => {
    setSession(null);
  }, []);
  const {
    isSupported: pictureInPictureSupported,
    pipWindow,
    open: openPictureInPicture,
    close: closePictureInPicture,
  } = useDocumentPictureInPicture({
    ...IN_APP_ASSESSMENT_PIP_WINDOW_SIZE,
    onUserDismiss: dismissAssessment,
  });

  const open = useCallback(
    async (next: CoachPortalSession) => {
      await openPictureInPicture();
      setSession(next);
    },
    [openPictureInPicture],
  );

  const close = useCallback(() => {
    closePictureInPicture();
    setSession(null);
  }, [closePictureInPicture]);

  const value = useMemo(() => ({ openSessionId: session?.id ?? null, open, close }), [session?.id, open, close]);

  return (
    <InAppAssessmentContext.Provider value={value}>
      {children}
      {/* Keyed so switching classes starts a fresh run rather than inheriting the previous timers. */}
      {session && (
        <InAppAssessmentWindow
          key={session.id}
          session={session}
          open
          onClose={close}
          pipWindow={pipWindow}
          pictureInPictureSupported={pictureInPictureSupported}
          onEnterPictureInPicture={openPictureInPicture}
        />
      )}
    </InAppAssessmentContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useInAppAssessment(): InAppAssessmentContextValue {
  const context = useContext(InAppAssessmentContext);
  if (!context) {
    throw new Error('useInAppAssessment must be used inside an InAppAssessmentProvider');
  }
  return context;
}
